// src/App.js - Complete with Route Protection Matching Sidebar
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import Overview from './pages/Overview';
import Drivers from './pages/Drivers';
import DriverDetails from './pages/DriverDetails';
import DriverPerformance from './pages/DriverPerformance';
import Customers from './pages/Customers';
import CustomerDetails from './pages/CustomerDetails';
import Rides from './pages/Rides';
import RideDetails from './pages/RideDetails';
import Analytics from './pages/Analytics';
import RevenueAnalytics from './pages/RevenueAnalytics';
import Earnings from './pages/Earnings';
import Vouchers from './pages/Vouchers';
import Support from './pages/Support';
import Login from './pages/Login';
import Notifications from './pages/Notifications';
import LiveMap from './pages/LiveMap';
import AuditLog from './pages/AuditLog';
import Reports from './pages/Reports';
import UserManagement from './pages/UserManagement';

// Helper to get current user role
const getUserRole = () => {
  try {
    const adminData = localStorage.getItem('admin');
    if (adminData) {
      const user = JSON.parse(adminData);
      return user.role || user.role_name || 'admin';
    }
  } catch (e) {
    console.error('Error getting user role:', e);
  }
  return 'admin';
};

// Define which roles can access which pages - MUST MATCH Sidebar
const pagePermissions = {
  // Overview - Everyone
  '/': ['super_admin', 'admin', 'finance', 'support', 'operations', 'viewer'],
  
  // Live Map - Only super_admin, admin, operations
  '/live-map': ['super_admin', 'admin', 'operations'],
  
  // Drivers - super_admin, admin, operations, support, viewer
  '/drivers': ['super_admin', 'admin', 'operations', 'support', 'viewer'],
  '/driver-performance': ['super_admin', 'admin', 'operations'],
  
  // Customers - Everyone except maybe not viewer? Let's include all that have it in sidebar
  '/customers': ['super_admin', 'admin', 'operations', 'support', 'finance', 'viewer'],
  '/customers/:id': ['super_admin', 'admin', 'operations', 'support', 'finance', 'viewer'],
  
  // Rides - super_admin, admin, operations, support, viewer
  '/rides': ['super_admin', 'admin', 'operations', 'support', 'viewer'],
  '/rides/:id': ['super_admin', 'admin', 'operations', 'support', 'viewer'],
  
  // Analytics - super_admin, admin, viewer
  '/analytics': ['super_admin', 'admin', 'viewer'],
  
  // Revenue - super_admin, admin, finance
  '/revenue-analytics': ['super_admin', 'admin', 'finance'],
  
  // Earnings - super_admin, admin, finance
  '/earnings': ['super_admin', 'admin', 'finance'],
  
  // Vouchers - super_admin, admin, finance
  '/vouchers': ['super_admin', 'admin', 'finance'],
  
  // Notifications - super_admin, admin
  '/notifications': ['super_admin', 'admin'],
  
  // Support - super_admin, admin, support, operations
  '/support': ['super_admin', 'admin', 'support', 'operations'],
  
  // User Management - ONLY super_admin
  '/users': ['super_admin'],
  
  // Audit Log - ONLY super_admin
  '/audit': ['super_admin'],
  
  // Reports - super_admin, admin, finance, viewer
  '/reports': ['super_admin', 'admin', 'finance', 'viewer'],
};

// Check if user can access a page
const canAccessPage = (path, userRole) => {
  // Super admin can access everything
  if (userRole === 'super_admin') return true;
  
  console.log(`🔍 Checking access for: ${path} with role: ${userRole}`);
  
  // Find matching permission
  for (const [pattern, roles] of Object.entries(pagePermissions)) {
    // Handle dynamic routes like /customers/:id
    const regexPattern = pattern.replace(/:\w+/g, '[^/]+');
    const regex = new RegExp(`^${regexPattern}$`);
    if (regex.test(path)) {
      const hasAccess = roles.includes(userRole);
      console.log(`  ${pattern}: ${hasAccess ? '✅ ALLOWED' : '❌ DENIED'}`);
      return hasAccess;
    }
  }
  
  // Default: allow access to overview
  return path === '/';
};

// Protected Route Component
const ProtectedRoute = ({ path, element }) => {
  const userRole = getUserRole();
  const hasAccess = canAccessPage(path, userRole);
  
  if (!hasAccess) {
    console.log(`🚫 Redirecting: ${path} not accessible for ${userRole}`);
    return <Navigate to="/" replace />;
  }
  
  return element;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<MainLayout />}>
          <Route index element={<Overview />} />
          
          {/* All routes with protection */}
          <Route path="live-map" element={<ProtectedRoute path="/live-map" element={<LiveMap />} />} />
          <Route path="drivers" element={<ProtectedRoute path="/drivers" element={<Drivers />} />} />
          <Route path="drivers/:id" element={<ProtectedRoute path="/drivers/:id" element={<DriverDetails />} />} />
          <Route path="driver-performance" element={<ProtectedRoute path="/driver-performance" element={<DriverPerformance />} />} />
          <Route path="customers" element={<ProtectedRoute path="/customers" element={<Customers />} />} />
          <Route path="customers/:id" element={<ProtectedRoute path="/customers/:id" element={<CustomerDetails />} />} />
          <Route path="rides" element={<ProtectedRoute path="/rides" element={<Rides />} />} />
          <Route path="rides/:id" element={<ProtectedRoute path="/rides/:id" element={<RideDetails />} />} />
          <Route path="analytics" element={<ProtectedRoute path="/analytics" element={<Analytics />} />} />
          <Route path="revenue-analytics" element={<ProtectedRoute path="/revenue-analytics" element={<RevenueAnalytics />} />} />
          <Route path="earnings" element={<ProtectedRoute path="/earnings" element={<Earnings />} />} />
          <Route path="vouchers" element={<ProtectedRoute path="/vouchers" element={<Vouchers />} />} />
          <Route path="support" element={<ProtectedRoute path="/support" element={<Support />} />} />
          <Route path="notifications" element={<ProtectedRoute path="/notifications" element={<Notifications />} />} />
          <Route path="users" element={<ProtectedRoute path="/users" element={<UserManagement />} />} />
          <Route path="audit" element={<ProtectedRoute path="/audit" element={<AuditLog />} />} />
          <Route path="reports" element={<ProtectedRoute path="/reports" element={<Reports />} />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;